import { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import type { Building } from '../types/Building';
import { useAwards } from '../hooks/useAwards';

// Fix for default markers in React-Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom building marker icon
const buildingIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#2563eb" width="24" height="24">
      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
    </svg>
  `),
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -30],
});

// Custom under-construction marker icon
const constructionIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#f59e0b" width="24" height="24">
      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
      <circle cx="18" cy="6" r="2" fill="#ef4444"/>
    </svg>
  `),
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -30],
});


// Component to fit all buildings in view
function FitBounds({ buildings }: { buildings: Building[] }) {
  const map = useMap();
  
  useState(() => {
    if (buildings.length > 0) {
      const buildingsWithCoords = buildings.filter(b => b.coordinates);
        if (buildingsWithCoords.length > 0) {
          const bounds = buildingsWithCoords
            .filter(b => b.coordinates)
            .map(b => [b.coordinates!.lat, b.coordinates!.lng] as [number, number]);
          if (bounds.length > 0) {
            map.fitBounds(bounds, { padding: [20, 20] });
          }
        }
    }
  });
  
  return null;
}

interface BuildingMapProps {
  buildings: Building[];
  selectedBuilding?: Building | null;
  onBuildingSelect?: (building: Building) => void;
}

export function BuildingMap({ buildings, selectedBuilding, onBuildingSelect }: BuildingMapProps) {
  const buildingsWithCoordinates = buildings.filter(building => building.coordinates);
  const { isAwarded } = useAwards();
  
  // NYC center as fallback
  const nycCenter: [number, number] = [40.7589, -73.9851];
  
  return (
    <div className="space-y-4">
      {/* Legend */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <h3 className="font-semibold text-gray-900 mb-3">Map Legend</h3>
        <div className="flex flex-wrap gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-blue-600"></div>
            <span>Completed Supertalls</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-amber-500"></div>
            <span>Under Construction</span>
          </div>

        </div>
      </div>
      
      <div className="w-full h-[600px] rounded-lg overflow-hidden shadow-lg">
        <MapContainer
        center={nycCenter}
        zoom={12}
        style={{ height: '100%', width: '100%' }}
        className="z-0"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <FitBounds buildings={buildingsWithCoordinates} />
        
        {buildingsWithCoordinates.map((building) => (
          <Marker
            key={building.rank}
            position={[building.coordinates?.lat || 0, building.coordinates?.lng || 0]}
            icon={building.status === 'Under construction' ? constructionIcon : buildingIcon}
            eventHandlers={{
              click: () => onBuildingSelect?.(building),
            }}
          >
            <Popup maxWidth={600} className="building-popup">
              <div className="p-3">
                <div className="flex items-start gap-3">
                  {building.image_url && (
                    <img
                      src={building.image_url}
                      alt={building.name}
                      className="w-20 h-20 object-cover rounded-md flex-shrink-0"
                      loading="lazy"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-lg text-gray-900 mb-1 leading-tight">
                      #{building.rank} {building.name}
                      {isAwarded(building.rank) && (
                        <span className="ml-2 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                          🏆 Chris Morgan Architectural Achievement Award
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2">{building.address}</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Height:</span>
                        <span className="font-medium">{building.height_feet}ft ({building.height_meters}m)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Floors:</span>
                        <span className="font-medium">{building.floors}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Status:</span>
                        <div className="font-medium">
                          {building.status === 'Under construction' ? (
                            <div>
                              <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
                                🚧 Under Construction
                              </span>
                              {building.expected_completion && (
                                <div className="text-xs text-gray-600 mt-1">
                                  Est. completion: {building.expected_completion}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                              Completed {building.year_completed}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Purpose:</span>
                        <span className="font-medium">{building.purpose}</span>
                      </div>
                    </div>
                    {building.notes && (
                      <p className="text-xs text-gray-500 mt-2 italic">
                        {building.notes}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      </div>
    </div>
  );
}

// Walking tour helper component
export function WalkingTourStats({ buildings }: { buildings: Building[] }) {
  const buildingsWithCoords = buildings.filter(b => b.coordinates);
  
  // Group buildings by general area for walking tour planning
  const areas = {
    'Financial District': buildingsWithCoords.filter(b => 
      b.coordinates && b.coordinates.lat < 40.72 && b.coordinates.lng > -74.02
    ),
    'Midtown South': buildingsWithCoords.filter(b => 
      b.coordinates && b.coordinates.lat >= 40.72 && b.coordinates.lat < 40.75 && b.coordinates.lng > -73.99
    ),
    'Midtown': buildingsWithCoords.filter(b => 
      b.coordinates && b.coordinates.lat >= 40.75 && b.coordinates.lat < 40.78 && b.coordinates.lng > -73.99
    ),
    'Hudson Yards': buildingsWithCoords.filter(b => 
      b.coordinates && b.coordinates.lng <= -73.99
    ),
    'Brooklyn': buildingsWithCoords.filter(b => 
      b.coordinates && b.coordinates.lat < 40.70
    )
  };
  
  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">🚶‍♂️ Walking Tour Guide</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {Object.entries(areas).map(([area, areaBuildings]) => (
          <div key={area} className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">{area}</h3>
            <p className="text-2xl font-bold text-blue-600 mb-1">{areaBuildings.length}</p>
            <p className="text-sm text-gray-500">buildings</p>
            {areaBuildings.length > 0 && (
              <div className="mt-2 text-xs text-gray-600">
                <p>Tallest: {areaBuildings.sort((a, b) => b.height_feet - a.height_feet)[0]?.name}</p>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-4 p-4 bg-blue-50 rounded-lg">
        <h4 className="font-semibold text-blue-900 mb-2">💡 Walking Tour Tips:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Start in Financial District, work your way north</li>
          <li>• Hudson Yards is a separate cluster - plan accordingly</li>
          <li>• Midtown has the highest concentration of buildings</li>
          <li>• Brooklyn Tower requires a separate trip</li>
        </ul>
      </div>
    </div>
  );
}